import type { QueryDSL, QueryResult, DatasetType } from '../types/QueryTypes';
import { executeQuery } from '../query/QueryExecutor';
import { getSearchIndex } from '../search/SearchIndex';
import { planQuery } from '../planner/QueryPlanner';
import { callExplanation } from '../llm/BedrockClient';
import { buildExplanationPrompt } from '../prompts/PlannerPrompt';
export type PipelineStage = 'planning' | 'validating' | 'executing' | 'explaining' | 'complete' | 'error';
export interface NlqResult {
  stage: PipelineStage;
  query?: QueryDSL;
  results?: QueryResult;
  explanation?: string;
  error?: string;
  suggestion?: string;
}
function shouldExplain(query: QueryDSL): boolean {
  return !!(query.groupBy || query.aggregate);
}
export async function runNlqPipeline(userQuestion: string, requestData: Record<string, unknown>[], touchpointData: Record<string, unknown>[], onStage?: (stage: PipelineStage) => void, signal?: AbortSignal): Promise<NlqResult> {
  onStage?.('planning');
  const plannerResult = await planQuery(userQuestion, signal);
  console.log('[NLQ] Pipeline planner result type:', plannerResult.type);
  if (plannerResult.type === 'error') {
    return {
      stage: 'error',
      error: plannerResult.error
    };
  }
  if (plannerResult.type === 'unsupported') {
    return {
      stage: 'error',
      suggestion: plannerResult.unsupported?.suggestion,
      error: 'I could not convert your question to a query.'
    };
  }
  const query = plannerResult.query!;
  console.log('[NLQ] Pipeline proceeding to execution with query:', JSON.stringify(query));
  onStage?.('validating');
  onStage?.('executing');
  const searchFn = (q: string, dataset: DatasetType) => {
    return getSearchIndex().search(q, dataset);
  };
  let results: QueryResult;
  try {
    results = executeQuery(query, requestData, touchpointData, searchFn);
    console.log('[NLQ] Execution complete:', results.metadata.totalRows, 'rows in', results.metadata.executionTime.toFixed(1), 'ms');
  } catch (err) {
    console.error('[NLQ] executeQuery threw:', err);
    return {
      stage: 'error',
      error: `Execution failed: ${err instanceof Error ? err.message : String(err)}`
    };
  }
  if (shouldExplain(query)) {
    onStage?.('explaining');
    console.log('[NLQ] Starting explanation LLM call...');
    try {
      const explanationPrompt = buildExplanationPrompt(userQuestion, JSON.stringify(query), JSON.stringify(results.rows));
      const explanation = await callExplanation(explanationPrompt, signal);
      console.log('[NLQ] Explanation received:', explanation.length, 'chars');
      return {
        stage: 'complete',
        query,
        results,
        explanation
      };
    } catch (err) {
      console.error('[NLQ] Explanation call failed:', err);
      return {
        stage: 'complete',
        query,
        results,
        explanation: undefined
      };
    }
  }
  onStage?.('complete');
  return {
    stage: 'complete',
    query,
    results
  };
}